<!-- app-subdomain-creators\src\accountPages\CreatorMediaContentMainDrive.vue -->

<template>
  <div class="w-full mx-auto pb-8 px-8 border-none">
    <h1 class="text-lg font-semibold text-zinc-800 mb-4">Media Content</h1>

    <div class="flex gap-4 rounded-2xl lg:h-[calc(100vh-220px)]">
      <!-- Folder Sidebar -->
      <CreatorMediaFolderSidebar
        :folders="folders"
        :current-folder-id="currentFolderId"
        @select-folder="handleSelectFolder"
        @create-folder="handleCreateFolder"
      />

      <!-- Main Content -->
      <div class="flex-1 flex flex-col ">
        
        <!-- Breadcrumb -->
        <div class="flex items-center gap-2 text-sm text-zinc-500 px-4 py-2">
        <span
            class="cursor-pointer hover:text-[#2D8CFF]"
            @click="goToRoot"
        >Main</span>

        <template v-for="crumb in breadcrumbs" :key="crumb.id">
            <span>/</span>
            <span
            class="cursor-pointer hover:text-[#2D8CFF]"
            @click="handleSelectFolder(crumb.id)"
            >
            {{ crumb.name }}
            </span>
        </template>
        </div>


        <div class="flex items-center justify-between px-4 py-3">
          <div class="flex items-center gap-2">
            <button
              class="px-3 py-1.5 text-xs rounded-full border border-zinc-200 text-zinc-600 hover:bg-zinc-50"
              @click="refreshCurrentFolder"
            >
              Refresh
            </button>
          </div>

          <div class="flex items-center gap-2">
            <button
              class="px-5 py-2 text-base rounded-full bg-[#2D8CFF] text-white hover:bg-[#2d8bffc4]"
              @click="showUploadModal = true"
            >
              + Upload Content
            </button>
          </div>
        </div>

        <!-- Files Grid -->
        <div class="flex-1 p-4 overflow-y-auto scrollbar scrollbar-thumb-zinc-100 scrollbar-track-zinc-50 dark:scrollbar-track-zinc-500 scrollbar-thumb-rounded-full scrollbar-track-rounded-full scrollbar-hover:scrollbar-track-zinc-300 relative"
        @dragover.prevent="onDragOver"
      @dragleave="onDragLeave"
      @drop="onDrop">

            <!-- DROPZONE OVERLAY -->
        <div
        v-if="isDragging"
        class="absolute inset-0 z-40 bg-blue-100/70 border-2 border-[#2D8CFF] border-dashed rounded-2xl
                flex items-center justify-center pointer-events-none"
        >
        <div class="text-center pointer-events-none mx-auto">
            <CloudArrowUpIcon class="h-18 w-18 text-[#2D8CFF]"/>
            
            <p class="text-lg font-semibold text-[#2D8CFF] mt-2">Drop files to upload</p>
        </div>
        </div>


          <div v-if="isLoading" class="text-sm text-zinc-500">
            Loading media...
          </div>

          <div v-else-if="mediaLoadError" class="rounded-2xl border border-amber-200 bg-amber-50 px-6 py-8 text-center text-sm text-amber-900">
            <p>{{ mediaLoadError }}</p>
            <button type="button" class="mt-3 font-semibold text-blue-700 hover:underline" @click="currentFolderId ? refreshCurrentFolder() : initializeMediaLibrary()">Try again</button>
          </div>

          <section v-else-if="files.length === 0 && childFolders.length === 0" class="overflow-hidden rounded-[28px] border border-zinc-200 bg-white shadow-sm">
            <div class="grid min-h-[460px] lg:grid-cols-[1.05fr_0.95fr]">
              <div class="flex flex-col justify-center px-6 py-9 sm:px-9 lg:px-11">
                <span class="mb-5 inline-flex w-fit items-center gap-2 rounded-full bg-blue-50 px-3 py-1.5 text-xs font-bold uppercase tracking-[0.14em] text-blue-700">
                  <PhotoIcon class="h-4 w-4" />
                  Media Content · Drive
                </span>
                <h2 class="max-w-xl text-3xl font-bold tracking-tight text-zinc-950 sm:text-4xl">
                  Give your content a home.
                </h2>
                <p class="mt-5 max-w-xl text-base leading-7 text-zinc-600">
                  Upload your first images, videos, or files, then organize them in folders. Your media stays ready for the member experiences and products you build.
                </p>
                <div class="mt-8 flex flex-col gap-3 sm:flex-row">
                  <button type="button" class="inline-flex items-center justify-center gap-2 rounded-xl bg-[#2478ff] px-5 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-blue-600" @click="showUploadModal = true">
                    <CloudArrowUpIcon class="h-4 w-4" />
                    Upload content
                  </button>
                  <button type="button" class="inline-flex items-center justify-center gap-2 rounded-xl border border-zinc-300 bg-white px-5 py-3 text-sm font-semibold text-zinc-800 transition hover:bg-zinc-50" @click="handleCreateFolder">
                    <FolderIcon class="h-4 w-4" />
                    Create a folder
                  </button>
                </div>
                <div class="mt-9 grid gap-4 border-t border-zinc-100 pt-7 sm:grid-cols-3">
                  <div v-for="item in mediaEmptySteps" :key="item.label" class="flex gap-3 sm:block">
                    <span class="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-zinc-950 text-xs font-bold text-white">{{ item.step }}</span>
                    <div class="sm:mt-3">
                      <p class="text-sm font-semibold text-zinc-900">{{ item.label }}</p>
                      <p class="mt-1 text-xs leading-5 text-zinc-500">{{ item.copy }}</p>
                    </div>
                  </div>
                </div>
              </div>
              <div class="relative flex min-h-[330px] items-center justify-center overflow-hidden bg-gradient-to-br from-[#ecf4ff] via-white to-violet-100 p-6 sm:p-9">
                <div class="absolute -right-16 -top-16 h-64 w-64 rounded-full bg-blue-300/30 blur-3xl" />
                <div class="absolute -bottom-20 -left-16 h-64 w-64 rounded-full bg-violet-300/30 blur-3xl" />
                <div class="relative w-full max-w-[390px] rounded-[26px] border border-white/80 bg-white/85 p-5 shadow-2xl shadow-blue-900/10 backdrop-blur">
                  <div class="flex items-center justify-between border-b border-zinc-100 pb-4">
                    <div>
                      <p class="text-[11px] font-bold uppercase tracking-[0.15em] text-blue-600">Your media library</p>
                      <p class="mt-1 text-lg font-semibold text-zinc-900">Content, organized</p>
                    </div>
                    <span class="flex h-10 w-10 items-center justify-center rounded-xl bg-blue-50 text-blue-700"><FolderIcon class="h-5 w-5" /></span>
                  </div>
                  <div class="mt-4 grid grid-cols-3 gap-3">
                    <div v-for="item in mediaPreviewTiles" :key="item.label" class="rounded-xl border border-zinc-100 bg-white p-2">
                      <div class="flex aspect-square items-center justify-center rounded-lg" :class="item.tileClass">
                        <component :is="item.icon" class="h-8 w-8" />
                      </div>
                      <p class="mt-2 text-center text-xs font-medium text-zinc-700">{{ item.label }}</p>
                    </div>
                  </div>
                  <div class="mt-4 flex items-center gap-2 rounded-xl bg-blue-50 px-4 py-3 text-xs font-medium text-blue-800">
                    <CloudArrowUpIcon class="h-4 w-4 shrink-0" />
                    Upload once. Use across your workspace.
                  </div>
                </div>
              </div>
            </div>
          </section>

          <div v-else class="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">

            <!-- FOLDER GRID ITEM -->
            <div
            v-for="folder in childFolders"
            :key="'folder-' + folder.id"
            @dblclick="handleSelectFolder(folder.id)"
            class="relative group rounded-xl overflow-hidden bg-zinc-100 hover:bg-zinc-100 transition cursor-pointer"

            >
            <!-- ICON CENTER -->
            <div class="absolute inset-0 flex items-center justify-center">
                <FolderIcon class="h-32 w-32 text-zinc-300 group-hover:text-zinc-400 transition" />
            </div>

            <!-- NAME OVERLAY BAR -->
            <div
                class="absolute bottom-0 left-0 right-0 bg-[#2D8CFF] text-white text-[12px]
                    px-2 py-1 backdrop-blur-sm group-hover:bg-[#1a81ff] transition text-center"
            >
                <p class="truncate">{{ folder.name }}</p>
            </div>
            </div>



            <div
                v-for="file in files"
                :key="file.id"
                class="rounded-xl overflow-hidden bg-[#2d8bff1a] hover:bg-[#2d8bff33] transition cursor-pointer group"
                @dblclick="openPreview(file)"
                >

<div
  class="w-full bg-zinc-200 relative flex items-center justify-center overflow-hidden"
  style="height: 140px;"
>

  <!-- IMAGE FILE -->
  <img
    v-if="file.file_type === 'image'"
    :src="file.secure_image_url"
    
    class="max-h-full max-w-full object-cover w-full h-full group-hover:scale-[1.25] transition-transform duration-700"
  />

  <!-- VIDEO FILE -->
  <template v-else-if="file.file_type === 'video'">

    <!-- READY -->
    <img
      v-if="file.thumbnail_url && file.status === 'ready'"
      :src="file.secure_thumbnail_url"
      
      class="max-h-full max-w-full object-cover w-full h-full group-hover:scale-[1.25] transition-transform duration-700"
    />

    <!-- PROCESSING -->
    <div
      v-else
      class="w-full h-full bg-zinc-300 flex items-center justify-center animate-pulse"
    >
      <span class="text-[11px] text-zinc-600 backdrop-blur-sm px-2 py-1 rounded-md bg-white/40">
        Processing…
      </span>
    </div>

  </template>

  <!-- OTHER FILE TYPES -->
  <div v-else class="text-xs text-zinc-500">
    {{ file.file_type.toUpperCase() }}
  </div>

</div>



              <div class="px-2 py-1.5">
                <p class="text-[12px] font-semibold text-zinc-800 truncate">
                  {{ file.file_name }}
                </p>

                <div class="flex justify-between items-center">
                <p class="text-[10px] text-zinc-400">
                  {{ (file.size_bytes / 1024 / 1024).toFixed(2) }} MB
                </p>
                <button
                    class="text-[10px] text-red-500 hover:text-red-600"
                    @click.stop="openDeleteConfirmation(file.id)"
                  >
                    Delete
                  </button>

                </div>

              </div>

            </div>
          </div>
        </div>


      </div>
    </div>


<!-- FILE PREVIEW MODAL -->
<TransitionRoot as="template" :show="previewOpen">
  <Dialog class="relative z-50" @close="previewOpen = false">
    
    <!-- Overlay -->
    <TransitionChild
      as="template"
      enter="ease-out duration-300"
      enter-from="opacity-0"
      enter-to="opacity-100"
      leave="ease-in duration-200"
      leave-from="opacity-100"
      leave-to="opacity-0"
    >
      <div class="fixed inset-0 bg-black bg-opacity-70 backdrop-blur-sm transition-opacity"></div>
    </TransitionChild>

    <!-- FLOATING CONTROLS ON OVERLAY (TOP-LEFT) -->
    <div class="fixed top-4 left-4 z-[60] flex items-center gap-3 text-white">
      <!-- Close button -->
      <button
        @click="previewOpen = false"
        class="h-9 w-9 flex items-center justify-center rounded-full
               bg-black/40 hover:bg-black/60 transition"
      >
        <svg xmlns="http://www.w3.org/2000/svg"
             class="h-5 w-5"
             viewBox="0 0 24 24"
             fill="none" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>

      <!-- File icon + name -->
      <div class="flex items-center gap-2 px-3 py-1.5 rounded-full bg-black/40 backdrop-blur">
        <!-- Icon -->
        <div class="flex items-center justify-center">
          <svg v-if="selectedFile?.file_type === 'image'"
            xmlns="http://www.w3.org/2000/svg"
            class="h-4 w-4 text-zinc-200"
            fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M4 16l4-4a2 2 0 013 0l4 4M14 12l2-2a2 2 0 013 0l1 1" />
          </svg>

          <svg v-else-if="selectedFile?.file_type === 'video'"
            xmlns="http://www.w3.org/2000/svg"
            class="h-4 w-4 text-zinc-200"
            fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14m-10 4h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
          </svg>

          <svg v-else
            xmlns="http://www.w3.org/2000/svg"
            class="h-4 w-4 text-zinc-200"
            fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M4 6h16M4 10h16M4 14h10" />
          </svg>
        </div>

        <!-- Name -->
        <p class="text-xs font-medium text-zinc-100 truncate max-w-[260px]">
          {{ selectedFile?.file_name }}
        </p>
      </div>
    </div>

    <!-- MODAL CONTENT (CENTERED PANEL) -->
    <div class="fixed inset-0 flex items-center justify-center p-4 z-50">
      <TransitionChild
        as="template"
        enter="ease-out duration-300"
        enter-from="opacity-0 scale-95"
        enter-to="opacity-100 scale-100"
        leave="ease-in duration-200"
        leave-from="opacity-100 scale-100"
        leave-to="opacity-0 scale-95"
      >
        <DialogPanel
          class="relative w-full overflow-hidden"
        >
          <div class="flex items-center justify-center p-4">

            <!-- IMAGE -->
            <img
              v-if="selectedFile?.file_type === 'image'"
              :src="`${apiProxyUrl}/api/image/${selectedFile.file_id}`"
              class="max-h-[90vh] max-w-full object-contain"
            />

            <!-- VIDEO with fixed aspect ratio & big size -->
            <div
            v-else-if="selectedFile?.file_type === 'video'"
            class="w-[90vw]  h-[90vh] bg-transparent rounded-xl overflow-hidden flex items-center justify-center"
            >
            
            <PlatformaVideoPlayer :fileId="selectedFile.file_id" />


            </div>

            <!-- FALLBACK -->
            <div v-else class="p-8 text-center text-zinc-300">
              Cannot preview this file type.
            </div>

          </div>
        </DialogPanel>
      </TransitionChild>
    </div>
  </Dialog>
</TransitionRoot>



    <!-- DELETE FILE CONFIRMATION MODAL -->
    <TransitionRoot as="template" :show="confirmOpen">
    <Dialog class="relative z-50" @close="confirmOpen = false">
        <TransitionChild
        as="template"
        enter="ease-out duration-300"
        enter-from="opacity-0"
        enter-to="opacity-100"
        leave="ease-in duration-200"
        leave-from="opacity-100"
        leave-to="opacity-0"
        >
        <div class="fixed inset-0 bg-black bg-opacity-75 backdrop-blur-sm transition-opacity" />
        </TransitionChild>

        <div class="fixed top-[30%] inset-x-0 z-10 w-screen overflow-y-auto">
        <div class="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <TransitionChild
            as="template"
            enter="ease-out duration-300"
            enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            enter-to="opacity-100 translate-y-0 sm:scale-100"
            leave="ease-in duration-200"
            leave-from="opacity-100 translate-y-0 sm:scale-100"
            leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
            <DialogPanel
                class="relative transform overflow-hidden rounded-lg bg-white px-5 pb-5 pt-6 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6"
            >
                <div class="sm:flex sm:items-start">
                <div
                    class="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10"
                >
                    <ExclamationTriangleIcon class="h-6 w-6 text-red-600" aria-hidden="true" />
                </div>
                <div class="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
                    <DialogTitle
                    as="h3"
                    class="text-base font-semibold leading-6 text-gray-900"
                    >
                    Delete File
                    </DialogTitle>
                    <div class="mt-2">

                    
                    <p class="text-sm text-gray-500">
                    Are you sure you want to permanently delete:
                    <br />
                    <strong class="text-gray-800">{{ fileToDeleteName }}</strong>
                    <br /><br />
                    This action cannot be undone.
                    </p>
                    
                    
                    </div>
                </div>
                </div>

                <div class="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                <button
                    type="button"
                    class="inline-flex w-full justify-center rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-500 sm:ml-3 sm:w-auto"
                    @click="confirmDelete"
                >
                    <svg
                    v-if="isProcessing"
                    class="animate-spin h-4 w-4 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    >
                    <circle
                        class="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        stroke-width="4"
                    />
                    <path
                        class="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8v4l3-3-3-3v4a8 8 0 100 16v-4l-3 3 3 3v-4a8 8 0 01-8-8z"
                    />
                    </svg>
                    <span v-else>Delete File</span>
                </button>

                <button
                    type="button"
                    class="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
                    @click="confirmOpen = false"
                >
                    Cancel
                </button>
                </div>
            </DialogPanel>
            </TransitionChild>
        </div>
        </div>
    </Dialog>
    </TransitionRoot>


    <!-- Upload Modal -->
    <CreatorMediaUploadModal
      :show="showUploadModal"
      :folder-id="currentFolderId"
      @close="showUploadModal = false"
      @uploaded="refreshCurrentFolder"
    />

    <!-- Create New Folder Modal -->
    <CreatorMediaNewFolderModal
  :show="showNewFolderModal"
  @close="showNewFolderModal = false"
  @create="confirmNewFolder"
/>

  </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue';
import {
  Dialog,
  DialogPanel,
  DialogTitle,
  TransitionRoot,
  TransitionChild
} from "@headlessui/vue";
import CreatorMediaFolderSidebar from '@/Components/CreatorMediaFolderSidebar.vue';
import CreatorMediaUploadModal from '@/Components/CreatorMediaUploadModal.vue';
import CreatorMediaNewFolderModal from '@/Components/CreatorMediaNewFolderModal.vue';
import PlatformaVideoPlayer from '@/Components/PlatformaVideoPlayer.vue';
import { useMediaUploadStore } from "@/stores/mediaUploadStore";
import { CloudArrowUpIcon, FolderIcon } from "@heroicons/vue/24/solid";
import { ExclamationTriangleIcon, PhotoIcon, FilmIcon, DocumentTextIcon } from "@heroicons/vue/24/outline";

const uploadStore = useMediaUploadStore();

const apiProxyUrl = process.env.VUE_APP_API_PROXY_URL;

const folders = ref([]);
const files = ref([]);
const currentFolderId = ref(null);
const isLoading = ref(true);
const mediaLoadError = ref('');
const mediaEmptySteps = [
  { step: 1, label: 'Upload', copy: 'Add images, videos, and files.' },
  { step: 2, label: 'Organize', copy: 'Keep everything easy to find.' },
  { step: 3, label: 'Create', copy: 'Use your media in member products.' },
];
const mediaPreviewTiles = [
  { label: 'Images', icon: PhotoIcon, tileClass: 'bg-blue-100 text-blue-600' },
  { label: 'Videos', icon: FilmIcon, tileClass: 'bg-violet-100 text-violet-600' },
  { label: 'Files', icon: DocumentTextIcon, tileClass: 'bg-cyan-100 text-cyan-600' },
];
const showUploadModal = ref(false);
const previewOpen = ref(false);
const selectedFile = ref(null);
const showNewFolderModal = ref(false);
const pendingFolderParent = ref(null);

const openPreview = (file) => {
  selectedFile.value = file;
  previewOpen.value = true;
};



/* ---------------------------------------------
   LOAD FOLDERS
--------------------------------------------- */
const loadFolders = async () => {
  try {
    const res = await fetch(`${apiProxyUrl}/api/list-folders`, {
      method: 'GET',
      credentials: 'include',
    });
    if (!res.ok) throw new Error(`Failed to load folders (${res.status})`);
    const data = await res.json();
    folders.value = data.folders || [];
    return true;
  } catch (error) {
    console.error('Failed to load folders', error);
    mediaLoadError.value = 'We could not load your media library right now.';
    return false;
  }
};

/* ---------------------------------------------
   ENSURE MAIN FOLDER EXISTS
--------------------------------------------- */
const ensureMainFolder = async () => {
  if (!await loadFolders()) return false;

  // If no folders for this user → create MAIN
  if (!folders.value.length) {
    const createRes = await fetch(`${apiProxyUrl}/api/create-folder`, {
      method: 'POST',
      credentials: 'include',
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ name: 'Main', parentId: null }),
    });

    if (!createRes.ok) {
      console.error("Failed to create Main folder");
      return false;
    }

    if (!await loadFolders()) return false;
  }

  // Assign current folder
  const main = folders.value.find(f => f.name === 'Main' && !f.parent_id);
  currentFolderId.value = main ? main.id : folders.value[0]?.id || null;
  return Boolean(currentFolderId.value);
};

/* ---------------------------------------------
   LOAD FILES IN CURRENT FOLDER
--------------------------------------------- */
const loadFiles = async () => {
  if (!currentFolderId.value) {
    isLoading.value = false;
    mediaLoadError.value = 'We could not open your media library right now.';
    return;
  }
  isLoading.value = true;
  mediaLoadError.value = '';
  try {
    const res = await fetch(
      `${apiProxyUrl}/api/list-files?folderId=${currentFolderId.value}`,
      { method: 'GET', credentials: 'include' }
    );
    if (!res.ok) throw new Error(`Failed to load files (${res.status})`);
    const data = await res.json();
    files.value = data.files || [];
  } catch (error) {
    console.error('Failed to load files', error);
    mediaLoadError.value = 'We could not load this folder right now.';
  } finally {
    isLoading.value = false;
  }
};

/* ---------------------------------------------
   SELECT FOLDER
--------------------------------------------- */
const handleSelectFolder = async (folderId) => {
  currentFolderId.value = folderId;
  await loadFiles();
};

/* ---------------------------------------------
   CREATE NEW FOLDER
--------------------------------------------- */
const handleCreateFolder = () => {
  pendingFolderParent.value = currentFolderId.value;
  showNewFolderModal.value = true;
};

const confirmNewFolder = async (folderName) => {
  showNewFolderModal.value = false;

  const res = await fetch(`${apiProxyUrl}/api/create-folder`, {
    method: 'POST',
    credentials: 'include',
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      name: folderName,
      parentId: pendingFolderParent.value,
    }),
  });

  if (!res.ok) {
    console.error("Failed to create folder");
    return;
  }

  await refreshCurrentFolder();
};



/* ---------------------------------------------
   DELETE FILE
--------------------------------------------- */


/* ---------------------------------------------
   REFRESH CURRENT FOLDER
--------------------------------------------- */
const refreshCurrentFolder = async () => {
  if (!await loadFolders()) return;
  await loadFiles();
};


const isDragging = ref(false);
let dragCounter = 0;

const onDragOver = (e) => {
  e.preventDefault();
  dragCounter++;
  isDragging.value = true;
};

const onDragLeave = () => {
  dragCounter--;
  if (dragCounter <= 0) {
    isDragging.value = false;
    dragCounter = 0;
  }
};

const onDrop = async (e) => {
  e.preventDefault();
  isDragging.value = false;
  dragCounter = 0;

  const filesDropped = e.dataTransfer.files;
  if (!filesDropped || !filesDropped.length) return;

  // 🔥 Upload directly using store logic (same as modal)
  await uploadStore.addFiles(filesDropped, currentFolderId.value);

  // refresh file list after upload
  await refreshCurrentFolder();
};

const confirmOpen = ref(false);
const fileToDelete = ref(null);
const fileToDeleteName = ref("");
const isProcessing = ref(false);

const openDeleteConfirmation = (fileId) => {
  fileToDelete.value = fileId;

  const file = files.value.find(f => f.id === fileId);
  fileToDeleteName.value = file ? file.file_name : "";

  confirmOpen.value = true;
};

const confirmDelete = async () => {
  if (!fileToDelete.value) return;

  isProcessing.value = true;

  const res = await fetch(`${apiProxyUrl}/api/delete-file/${fileToDelete.value}`, {
    method: 'DELETE',
    credentials: 'include',
  });

  isProcessing.value = false;
  confirmOpen.value = false;

  if (!res.ok) {
    console.error("Failed to delete file");
    return;
  }

  await loadFiles();
};

const childFolders = computed(() =>
  folders.value.filter(f => f.parent_id === currentFolderId.value)
);


const breadcrumbs = computed(() => {
  let chain = [];
  let current = currentFolderId.value;

  while (current) {
    const folder = folders.value.find(f => f.id === current);
    if (!folder) break;

    chain.unshift(folder);
    current = folder.parent_id;
  }

  // remove Main (root) if you don’t want it duplicated
  return chain.filter(f => f.name !== 'Main');
});

const goToRoot = () => {
  const main = folders.value.find(f => f.name === 'Main' && !f.parent_id);
  if (main) handleSelectFolder(main.id);
};


/* ---------------------------------------------
   INITIAL LOAD
--------------------------------------------- */
const initializeMediaLibrary = async () => {
  try {
    isLoading.value = true;
    mediaLoadError.value = '';
    const ready = await ensureMainFolder();
    if (!ready) {
      mediaLoadError.value = 'We could not open your media library right now.';
      isLoading.value = false;
      return;
    }
    await loadFiles();
  } catch (error) {
    console.error('Failed to initialize media library', error);
    mediaLoadError.value = 'We could not open your media library right now.';
    isLoading.value = false;
  }
};

onMounted(initializeMediaLibrary);
</script>

